/**
 * Remark plugin that turns inline `<private />` markers in streamed
 * assistant text into private-chip nodes at markdown render time.
 *
 * Marks a private (organization-tailored) company result. Replaced
 * the bare 🔒 emoji marker (PLT-4548) and its first follow-up
 * `<lock />` directive (PLT-4600); PLT-4617 renamed the directive to
 * `<private />` and repositioned the chip to follow the company name
 * (`**Acme** <private />`). See `lock-badge-markers.ts` for the
 * marker grammar and the standalone (unit-testable) helpers, and
 * `upright-chat-tool-uis.tsx` for the `InlineLockBadge` React
 * component the rendered link node resolves to.
 *
 * Mirrors the `remarkCitationPills` / `remarkToneBadges` pattern: visit
 * each `text` node, swap `<private />` markers for link nodes (URL =
 * `lock:private`), and let the chat UI's `components.a` override
 * dispatch the link to the chip component.
 *
 * PLT-4661: also visit `inlineCode` nodes. The chat system prompt
 * forbids wrapping the directive in backticks, but models still leak
 * `` `<private />` `` occasionally. Markdown tokenises that as an
 * `inlineCode` node — invisible to a `text`-only visitor — so without
 * this second pass the raw tag reaches the user inside a code box and
 * the chip never renders. The server-side `syntax-leak-detector.ts`
 * already logs this case as `private_tag_in_code_fence` for drift
 * telemetry; the renderer treats it as recoverable input and renders
 * the chip anyway. Citation pills and tone badges have no equivalent
 * fallthrough today — the cite/badge prompt teaching does not invite
 * the model to backtick those tags the same way (`<cite>` is server-
 * inserted, `<badge>` carries a label that breaks the inline-code
 * single-line shape) — so the parity is deliberate; revisit if drift
 * telemetry shows the same shape there.
 *
 * PLT-4896: also visit `html` nodes. micromark/`remarkParse` may
 * tokenise a stand-alone `<private />` directive as an `html` mdast
 * node — invisible to a `text`-only visitor — particularly when the
 * tag sits at the start of a soft-wrapped line, immediately after a
 * `**bold**` span, or with whitespace inside the tag. We don't enable
 * `rehype-raw` (raw HTML is otherwise stripped by `react-markdown`),
 * so without this third pass the raw `<private />` characters bleed
 * straight into the rendered DOM. Recovery is the same shape as the
 * `inlineCode` fallthrough: run the splitter on `node.value` and swap
 * the html node for the resulting text/link sequence.
 */
import { SKIP, visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Html, InlineCode, Root, Text } from 'mdast';
import { splitTextOnLockMarkers, type MarkerSplitChild } from './lock-badge-markers';

export const remarkLockBadges: Plugin<[], Root> = () => {
  return tree => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnLockMarkers(node.value);
      if (newChildren === null) return;
      // mdast spec allows `link` wherever `text` is allowed; the parent
      // children type is a strict union that doesn't include `link` for
      // every variant (e.g. table cells), so cast to keep TS happy. Same
      // pattern as the citation and tone-badge plugins.
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
    // Second pass: tolerate backtick-wrapped markers. An `inlineCode`
    // node whose value matches a `<private />` directive (alone or
    // mixed with prose inside the same backtick span) is replaced with
    // the same text/link sequence the `text` visitor would produce.
    // Any prose that shared the backticks loses its code formatting —
    // a deliberate trade: the chip is the intent, the `code` styling
    // was an LLM mistake, and recovering the chip is worth more than
    // preserving the code box around it.
    visit(tree, 'inlineCode', (node: InlineCode, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnLockMarkers(node.value);
      if (newChildren === null) return;
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
    // Third pass (PLT-4896): tolerate html-tokenised markers. A bare
    // `<private />` directive tokenised by micromark as an `html`
    // mdast node never reaches the `text` visitor; without this pass
    // it would render as raw source characters because we don't run
    // `rehype-raw`. Replace the html node with the same text/link
    // sequence the `text` visitor would produce.
    visit(tree, 'html', (node: Html, index, parent) => {
      if (typeof index !== 'number' || !parent) return;
      const newChildren = splitTextOnLockMarkers(node.value);
      if (newChildren === null) return;
      (parent.children as unknown as MarkerSplitChild[]).splice(index, 1, ...newChildren);
      return [SKIP, index + newChildren.length];
    });
  };
};
